import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { ApiService } from '../api/api.service';
import { FacebookTrackingService } from '../tracking/facebook-tracking.service';
import { User } from '../../models/user.model';
import { GlobalAppState } from '../../../store/reducers';
import * as UserActions from '../../../store/actions/user.actions';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  token: string;
  refreshToken?: string;
  expiresIn?: number;
}

/** Raw shape returned by the new API */
interface ApiLoginResponse {
  meta: { status: number; total_results: number };
  data: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
    sessionId: string;
    user: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      brandId?: string;
      permissions?: string[];
      isSuperAdmin?: boolean;
    };
  };
}

export interface RefreshTokenResponse {
  token: string;
  expiresIn?: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiService: ApiService = inject(ApiService);
  private store: Store<GlobalAppState> = inject(Store);
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);
  // FacebookTrackingService gets enriched with the logged-in user's PII
  // (email, phone, name) whenever setUser fires, so upper-funnel events
  // (PageView/ViewContent/Lead) carry em/ph/fn/ln match keys and EMQ jumps
  // from ~6.8 to 9+ for the authenticated cohort.
  private facebookTracking = inject(FacebookTrackingService);

  private readonly TOKEN_KEY = 'halifax_token';
  private readonly REFRESH_TOKEN_KEY = 'halifax_refresh_token';
  private readonly USER_KEY = 'halifax_user';

  private currentUserSubject = new BehaviorSubject<User | null>(this.getStoredUser());
  public currentUser$ = this.currentUserSubject.asObservable();

  private tokenSubject = new BehaviorSubject<string | null>(this.getStoredToken());
  public token$ = this.tokenSubject.asObservable();

  constructor() {
    // Check if stored token is still valid on service initialization (browser only)
    if (this.isBrowser) {
      this.validateStoredToken();
      // If we have a token but no user, we should load user data
      const token = this.getStoredToken();
      const user = this.getStoredUser();
      if (token && !user) {
      }
    }
  }

  /**
   * Login with email and password
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.apiService.post<any>('auth/login', credentials).pipe(
      map(raw => this.mapLoginResponse(raw)),
      tap(response => {
        this.setAuthData(response);
      }),
      catchError(error => {
        this.clearAuthData();
        return throwError(() => error);
      })
    );
  }

  /**
   * Register a new account.
   *
   * Two possible outcomes:
   * - First-time registration: backend issues tokens; we store them and the
   *   resolved response carries the new user.
   * - Cross-brand claim (Phase 1.5): the email already has an identity in
   *   another brand. Backend creates an unverified identity, emails a claim
   *   link, and returns `{ data: { requiresVerification: true } }` with no
   *   tokens. We surface a flag so the UI can render "check your email".
   */
  register(payload: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
  }): Observable<LoginResponse | { requiresVerification: true; email: string }> {
    return this.apiService.post<any>('auth/register', payload).pipe(
      map(raw => {
        if (raw?.data?.requiresVerification) {
          return { requiresVerification: true as const, email: payload.email };
        }
        return this.mapLoginResponse(raw);
      }),
      tap(response => {
        if ('requiresVerification' in response) return;
        this.setAuthData(response);
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Map the API login response to LoginResponse.
   *
   * Phase 6.2: User model is now camelCase to match the API; this is almost
   * an identity map. Two things still differ from a pure spread:
   *  - flattening `data: { accessToken, ... }` (envelope) into `{ token, ... }`
   *  - deriving `role` from the JWT's isSuperAdmin claim
   */
  private mapLoginResponse(raw: any): LoginResponse {
    if (raw?.data?.accessToken && raw?.data?.user) {
      const apiUser = raw.data.user;
      const role: 'admin' | 'customer' = apiUser.isSuperAdmin ? 'admin' : 'customer';

      return {
        token: raw.data.accessToken,
        refreshToken: raw.data.refreshToken,
        expiresIn: raw.data.expiresIn,
        user: {
          id: apiUser.id,
          email: apiUser.email,
          firstName: apiUser.firstName,
          lastName: apiUser.lastName,
          role,
          permissions: apiUser.permissions,
        },
      };
    }

    // Legacy format passthrough — kept defensively for any older API shape
    // that might still surface in dev or from a stale build.
    if (raw?.token && raw?.user) {
      return raw as LoginResponse;
    }

    throw new Error('Invalid response format from login API');
  }

  /**
   * Logout user.
   *
   * Calls the backend's /auth/logout so the server-side AuthSession is
   * revoked (refresh token invalidated, SESSION_REVOKED audit entry written
   * per Phase 1.5). Body is empty — the backend defaults to the JWT's own
   * sessionId. Local cleanup runs regardless of the API outcome.
   */
  logout(): Observable<any> {
    const token = this.getToken();

    const logoutRequest = token
      ? this.apiService.post('auth/logout', {})
      : of(null);

    return logoutRequest.pipe(
      tap(() => {
        this.clearAuthData();
      }),
      catchError(() => {
        // Even if API logout fails (e.g. token already dead), clear local data.
        this.clearAuthData();
        return of(null);
      })
    );
  }

  /**
   * Refresh authentication token.
   *
   * Phase 4.1: the refresh token lives in an httpOnly cookie that the browser
   * attaches automatically on /auth/refresh (Path=/auth/refresh, SameSite=None,
   * withCredentials enabled in the API interceptor). The body is sent empty —
   * the backend reads the cookie. If no cookie is present, the backend
   * returns 401 and we tear down the local session in the catchError below.
   */
  refreshToken(): Observable<string> {
    return this.apiService.post<any>('auth/refresh', {}).pipe(
      map(raw => {
        // New API format: { meta, data: { accessToken, expiresIn, ... } }
        if (raw?.data?.accessToken) {
          return { token: raw.data.accessToken, expiresIn: raw.data.expiresIn } as RefreshTokenResponse;
        }
        // Legacy format
        return raw as RefreshTokenResponse;
      }),
      tap(response => {
        this.setToken(response.token);
        if (response.expiresIn) {
          this.scheduleTokenRefresh(response.expiresIn);
        }
      }),
      map(response => response.token),
      catchError(error => {
        this.clearAuthData();
        return throwError(() => error);
      })
    );
  }

  /**
   * Request a password reset email
   */
  forgotPassword(email: string): Observable<any> {
    return this.apiService.post('auth/forgot-password', { email });
  }

  /**
   * Reset password using token from email
   */
  resetPassword(token: string, newPassword: string): Observable<any> {
    return this.apiService.post('auth/reset-password', { token, newPassword });
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getCurrentUser();
    return !!(token && user && !this.isTokenExpired());
  }

  /**
   * Check if user has admin role
   */
  isAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.role === 'admin';
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Get current token
   */
  getToken(): string | null {
    const token = this.tokenSubject.value;
    return token;
  }

  /**
   * Set authentication data after successful login.
   *
   * Phase 4.1: refresh token no longer persisted to localStorage — it lives in
   * an httpOnly cookie set by the backend on /auth/login. The backend still
   * returns refreshToken in the JSON body during the dual-mode rollout, but
   * the frontend ignores it.
   */
  private setAuthData(response: LoginResponse): void {
    this.setToken(response.token);
    this.setUser(response.user);

    if (response.expiresIn && this.isBrowser) {
      this.scheduleTokenRefresh(response.expiresIn);
    }
  }

  /**
   * Handle automatic authentication after checkout (for new users)
   */
  handleCheckoutAuth(authToken: string, userData?: Partial<User>): void {
    this.setToken(authToken);

    // If we have user data, set it
    if (userData) {
      this.setUser(userData as User);
    }
  }

  /**
   * Set authentication data from checkout response
   */
  setCheckoutAuthData(authToken: string, customerEmail: string, customerName: string): void {
    this.setToken(authToken);

    // Create user object from checkout data
    const [firstName, ...lastNameParts] = customerName.split(' ');
    const lastName = lastNameParts.join(' ');

    const user: User = {
      email: customerEmail,
      firstName,
      lastName,
      role: 'customer',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    this.setUser(user);

    // Dispatch action to update NgRx store
    this.store.dispatch(UserActions.logInComplete({ user }));
  }

  /**
   * Set authentication token
   */
  private setToken(token: string): void {
    if (this.isBrowser) {
      localStorage.setItem(this.TOKEN_KEY, token);
      // Verify it was stored correctly
      const storedToken = localStorage.getItem(this.TOKEN_KEY);
    }
    this.tokenSubject.next(token);
  }

  /**
   * Set user data
   */
  private setUser(user: User): void {
    if (this.isBrowser) {
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    }
    this.currentUserSubject.next(user);
    // Propagate identity to the Meta tracking layer so subsequent events
    // (PageView, ViewContent, Lead, etc.) carry hashed em/ph/fn/ln + a
    // stable externalId based on the user UUID.
    this.facebookTracking.setCustomerData({
      email: user.email,
      phone: user.phone,
      firstName: user.firstName,
      lastName: user.lastName,
    });
    if (user.id) {
      this.facebookTracking.setExternalId(user.id);
    }
  }

  /**
   * Clear all authentication data
   */
  private clearAuthData(): void {
    if (this.isBrowser) {
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      localStorage.removeItem(this.USER_KEY);
    }

    this.tokenSubject.next(null);
    this.currentUserSubject.next(null);
    // Drop PII match keys (email/phone/name) and the stable external_id from
    // the Meta tracking layer. The anonymous visitor id (hl_visitor_id) is
    // deliberately preserved so cross-session attribution continuity stays
    // intact for the device.
    this.facebookTracking.clearCustomerData();
  }

  /**
   * Get stored token from localStorage
   */
  private getStoredToken(): string | null {
    return this.isBrowser ? localStorage.getItem(this.TOKEN_KEY) : null;
  }

  /**
   * Get stored user from localStorage
   */
  private getStoredUser(): User | null {
    if (!this.isBrowser) {
      return null;
    }

    const userStr = localStorage.getItem(this.USER_KEY);
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Check if token is expired by decoding the JWT payload
   */
  private isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) return true;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) return true; // Not a valid JWT

      const payload = JSON.parse(atob(parts[1]));
      if (!payload.exp) return false; // No expiry claim, assume valid

      // Token is expired if exp is in the past (with 30s buffer)
      return (payload.exp * 1000) < (Date.now() - 30000);
    } catch {
      return true;
    }
  }

  /**
   * Validate stored session on service initialization.
   *
   * Phase 4.1: the refresh token lives in an httpOnly cookie that survives
   * tab/browser close until its 30-day TTL. When the user comes back days
   * later, the in-memory + localStorage access token is dead but the cookie
   * is fine — we silently mint a new access token instead of forcing a
   * re-login.
   *
   * Decision tree:
   *  - No stored user      → user was never logged in; do nothing.
   *  - User + valid token  → already authenticated; do nothing.
   *  - User + dead/missing token → try /auth/refresh. The browser attaches
   *    the cookie automatically. Success → seamless restore. Failure (cookie
   *    expired or missing) → clear the stale user data so the UI shows the
   *    logged-out state.
   */
  private validateStoredToken(): void {
    if (!this.isBrowser) {
      return;
    }

    const user = this.getCurrentUser();
    if (!user) {
      // Nothing to restore.
      return;
    }

    const token = this.getToken();
    if (token && !this.isTokenExpired()) {
      // Still valid — nothing to do.
      return;
    }

    // Try cookie-backed silent refresh. Fire-and-forget; on success the
    // tokenSubject updates and any subscribed UI flips to "signed in".
    this.refreshToken().subscribe({
      error: () => {
        this.clearAuthData();
      },
    });
  }

  /**
   * Schedule automatic token refresh
   */
  private scheduleTokenRefresh(expiresIn: number): void {
    if (!this.isBrowser) {
      return;
    }

    // Refresh token 5 minutes before expiration
    const refreshTime = (expiresIn - 300) * 1000;

    setTimeout(() => {
      if (this.isAuthenticated()) {
        this.refreshToken().subscribe({
          error: () => this.clearAuthData()
        });
      }
    }, refreshTime);
  }
}
